import type { FC, ReactNode } from 'react';

import { Button, Icon, Stack, Text, cn } from '@eluve/components';

import type { FindingsGroupData } from './grouping';
import type { ConfidenceBand } from './types';

const DOT_CLASS: Record<ConfidenceBand, string> = {
  high: 'bg-bg-positive-strong-primary',
  medium: 'bg-bg-warning-strong-secondary',
  low: 'bg-bg-negative-strong-primary',
  unknown: 'bg-bg-accent-strong-primary',
};

export const FindingsGroup: FC<{
  group: FindingsGroupData;
  reviewedCount: number;
  approveAllDisabled?: boolean;
  onApproveAll: () => void;
  children: ReactNode;
}> = ({ group, reviewedCount, approveAllDisabled, onApproveAll, children }) => (
  <div className="w-full overflow-hidden rounded-xl border border-border-secondary bg-bg-primary">
    <Stack
      direction="horizontal"
      align="center"
      gap={2}
      className="border-b border-border-secondary px-4 py-3"
    >
      <span
        className={cn('size-2 shrink-0 rounded-full', DOT_CLASS[group.key])}
      />
      <Text size="sm" weight="semibold">
        {group.title}
      </Text>
      <Text size="xs" color="tertiary">
        {group.findings.length} {group.findings.length === 1 ? 'item' : 'items'}
      </Text>
      <span className="flex-1" />
      <Text size="xs" color="tertiary" className="tabular-nums">
        {reviewedCount}/{group.findings.length} reviewed
      </Text>
      <Button
        size="sm"
        variant="outline"
        disabled={approveAllDisabled}
        className="rounded-full border-border-positive-subtle bg-bg-positive-subtle text-text-positive hover:bg-bg-positive-subtle"
        onClick={onApproveAll}
      >
        <Icon name="Check" size="xs" />
        Approve all
      </Button>
    </Stack>
    <Stack gap={2} className="p-3">
      {children}
    </Stack>
  </div>
);
